import { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { EuiFlexGroup, EuiFlexItem, EuiSpacer, EuiText } from "@elastic/eui";
import moment from "moment-timezone";
import { useNavigate } from "react-router-dom";
import Modal from "components/Modal/Modal";
import ConfirmModal from "components/Modal/ConfirmModal";
import ProfileSteps from "./ProfileSteps";
import ProfileDetails from "./Details/ProfileDetails";
import ProfileResources from "./Resources/ProfileResources";
import ProfileAlertsAssign from "./Alerts/ProfileAlertsAssign";
import ProfileNotifications from "./Notifications/ProfileNotifications";
import AlertsService from "services/AdminConsole/Monitoring/Profiles/MonitoringProfilesService";
import NotificationService from "services/AdminConsole/Monitoring/NotificationChannels/NotificationService";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";
import Emitter from "services/UtilsService/Emitter";
import {
  AssignAlertTypeModel,
  AssignResourceModel,
  Profile,
  NewProfileData,
  Resource,
  SelectedResource,
  CheckAssignmentResult,
  AlertType,
  AssignNotificationChannelsModel,
  NotificationChannel,
} from "../profileTypes";
import Button from "components/Buttons/Button";
import { useFetch } from "hooks/useFetch";
import ProfileCompleteModal from "./ProfileCompleteModal";
import { REDIRECT_TO_NOTIFICATION_CHANNELS } from "settings/menu/admin_console";
import { useProfileStyles } from "../profileStyles";
import { PROFILE_STEPS } from "../profileConsts";
import { isNullOrUndefined } from "utils";

interface Props {
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  profiles: Profile[];
  isEditMode: boolean;
  currentProfile?: Profile;
  setExpandedIds: any;
  refetch: (
    params?:
      | Omit<
          {
            clientId: string;
          },
          "clientId"
        >
      | undefined
  ) => Promise<void>;
}

const ProfileForm = ({
  setIsModalOpen,
  profiles,
  currentProfile,
  refetch,
  isEditMode,
  setExpandedIds,
}: Props) => {
  const { clientId, ExceptionTopFilterObject } = useSelector(globalFilterSelector, shallowEqual);
  const {
    data: notificationData,
    isLoading: isLoadingNotificationChannels,
    refetch: refetchNotificationChannels,
  } = useFetch<{ clientId: string }, any>({
    serviceFn: NotificationService.getNotificationChannels,
    errorMessage: "Failed to fetch notification channels",
  });

  const {
    data: profilesData,
    isLoading: loading,
    refetch: getProfiles,
  } = useFetch<{ clientId: string }, any>({
    serviceFn: AlertsService.getProfiles,
    errorMessage: "Failed to fetch profiles",
    deps: [ExceptionTopFilterObject],
  });
  const classes = useProfileStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [descriptionTouched, setDescriptionTouched] = useState(false);
  const [profileDescription, setProfileDescription] = useState<NewProfileData>({
    existingProfile: null,
    name: "",
    activeTimes: "Eastern Standard Time",
    description: "",
    timeFrames: [
      {
        timePeriod: "daily",
        startTime: moment().startOf("day"),
        endTime: moment().endOf("day"),
      },
    ],
    isSyncedToClients: false,
  });
  const [selectedResources, setSelectedResources] = useState<SelectedResource[]>([]);
  const [selectedAlertTypes, setSelectedAlertTypes] = useState<AlertType[]>([]);
  const [selectedNotificationChannels, setSelectedNotificationChannels] = useState<{
    alertOpenAssignments: { severityTypeId: number; channels: { channelId: number }[] }[];
    alertCloseAssignments: { severityTypeId: number; channels: { channelId: number }[] }[];
  }>({
    alertOpenAssignments: [],
    alertCloseAssignments: [],
  });
  const [currentStep, setCurrentStep] = useState(1);
  const [createdProfileId, setCreatedProfileId] = useState<number | null>(null);
  const [isProfileCreatedModalOpen, setIsProfileCreatedModalOpen] = useState(false);
  const [isNotificationChannelModalOpen, setisNotificationChannelModalOpen] = useState(false);
  const [isCloseConfirmationModalOpen, setIsCloseConfirmationModalOpen] = useState(false);
  const [notificationChannels, setNotificationChannels] = useState<NotificationChannel[]>([]);
  const [isAlertRemovalConfirmModalOpen, setIsAlertRemovalConfirmModalOpen] = useState(false);
  const [removedAlertTypes, setRemovedAlertTypes] = useState<{ name: string; level: number }[]>([]);
  const [addedAlertTypes, setAddedAlertTypes] = useState<{ name: string; level: number }[]>([]);
  const [isAlertRemovalFromCloseFlow, setIsAlertRemovalFromCloseFlow] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchNotificationChannels = async () => {
      try {
        const result = await AlertsService.getNotificationChannels({ clientId });
        if (result?.channels) {
          setNotificationChannels(result.channels);
        }
      } catch (error) {
        console.error("Failed to fetch notification channels", error);
      }
    };

    fetchNotificationChannels();
  }, [clientId]);

  const isNameValid = profileDescription.name.length > 0;
  const showNameError = nameTouched && profileDescription.name.length === 0;
  const showDescriptionError = descriptionTouched && profileDescription.description.length === 0;
  const isSystemProfile = isEditMode && currentProfile?.isSystemProfile;

  const errorMsgs = {
    name: "Please enter a profile name",
    description: "Please enter a profile description",
  };

  useEffect(() => {
    if (notificationData && notificationData.length === 0) {
      setisNotificationChannelModalOpen(true);
    }
  }, [notificationData]);

  const getProfileDescriptionPayload = () => {
    const name = profileDescription.name;
    const timeZone = profileDescription.activeTimes;
    const description = profileDescription.description;
    const { isSyncedToClients } = profileDescription;

    type DayOfWeek =
      "Monday" | "Tuesday" | "Wednesday" | "Thursday" | "Friday" | "Saturday" | "Sunday";

    const weekdays: DayOfWeek[] = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
    const weekends: DayOfWeek[] = ["Saturday", "Sunday"];
    const allDays: DayOfWeek[] = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ];

    const activityWindows = profileDescription.timeFrames.map((timeFrame) => {
      const roundUpToNextMinute = (time: any) =>
        time.seconds() === 0
          ? time.format("HH:mm:ss")
          : time.add(1, "minute").startOf("minute").format("HH:mm:ss");

      return {
        startTime: roundUpToNextMinute(timeFrame.startTime),
        endTime: roundUpToNextMinute(timeFrame.endTime),
        daysOfWeek:
          timeFrame.timePeriod === "weekdays"
            ? weekdays
            : timeFrame.timePeriod === "weekends"
              ? weekends
              : allDays,
      };
    });

    return {
      clientId,
      name,
      description,
      timeZone,
      activityWindows,
      isSyncedToClients,
    };
  };

  const timeToMinutes = (timeStr: any) => {
    const [hours, minutes] = timeStr.split(":").slice(0, 2).map(Number);
    return hours * 60 + minutes;
  };

  const normalizeTimeSpan = (start: any, end: any) => {
    if (start === end) return [[0, 24 * 60]];
    return end < start
      ? [
          [start, 24 * 60],
          [0, end],
        ]
      : [[start, end]];
  };

  const doSpansOverlap = (spans1: any, spans2: any) => {
    for (const [s1, e1] of spans1)
      for (const [s2, e2] of spans2) if (Math.max(s1, s2) < Math.min(e1, e2)) return true;
    return false;
  };

  const hasOverlappingTimeFrames = (timeFrames: any) => {
    for (let i = 0; i < timeFrames.length; i++) {
      for (let j = i + 1; j < timeFrames.length; j++) {
        const f1 = timeFrames[i];
        const f2 = timeFrames[j];
        if (!f1.daysOfWeek.some((day: any) => f2.daysOfWeek.includes(day))) continue;
        if (
          (f1.startTime === "00:00:00" && f1.endTime === "00:00:00") ||
          (f2.startTime === "00:00:00" && f2.endTime === "00:00:00")
        )
          return true;
        const spans1 = normalizeTimeSpan(timeToMinutes(f1.startTime), timeToMinutes(f1.endTime));
        const spans2 = normalizeTimeSpan(timeToMinutes(f2.startTime), timeToMinutes(f2.endTime));
        if (doSpansOverlap(spans1, spans2)) return true;
      }
    }
    return false;
  };

  const createProfile = async () => {
    setIsLoading(true);

    const createProfilePayload = getProfileDescriptionPayload();

    const isDuplicateName = profiles.some((profile) => profile.name === createProfilePayload.name);
    if (isDuplicateName) {
      Emitter.emit(
        "displayFailure",
        "Profile name already exists. Please choose a different name."
      );
      setIsLoading(false);
      return;
    }

    if (hasOverlappingTimeFrames(createProfilePayload.activityWindows)) {
      Emitter.emit(
        "displayFailure",
        "Activity windows have overlapping hours. Please adjust the time frames."
      );
      setIsLoading(false);
      return;
    }

    try {
      const result = await AlertsService.createProfile(createProfilePayload);

      if (result) {
        setCreatedProfileId(result.id);
        // await refetch();
        await getProfiles();
        setExpandedIds([]);
        if (!isEditMode) setCurrentStep(currentStep + 1);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const updateProfileDescription = async () => {
    setIsLoading(true);

    const basePayload = getProfileDescriptionPayload();
    const { isSyncedToClients, ...payloadWithoutSync } = basePayload;

    const editProfilePayload = {
      ...payloadWithoutSync,
      monitoringProfileId: currentProfile?.id as number,
    };

    const isDuplicateName = profiles.some(
      (profile) => profile.name === editProfilePayload.name && profile.id !== currentProfile?.id
    );
    if (isDuplicateName) {
      Emitter.emit(
        "displayFailure",
        "Profile name already exists. Please choose a different name."
      );
      setIsLoading(false);
      return;
    }

    if (hasOverlappingTimeFrames(editProfilePayload.activityWindows)) {
      Emitter.emit(
        "displayFailure",
        "Activity windows have overlapping hours. Please adjust the time frames."
      );
      setIsLoading(false);
      return;
    }

    try {
      const result = await AlertsService.updateProfileDescription(editProfilePayload);

      if (result) {
        // await refetch();
        await getProfiles();
        setExpandedIds([]);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
      setIsDirty(false);
    }
  };

  const checkPreviousAssignments = (
    selectedResources: SelectedResource[]
  ): CheckAssignmentResult => {
    const currentProfileId = isEditMode
      ? (currentProfile?.id as number)
      : (createdProfileId as number);
    const assignedResources = selectedResources.filter(
      (i) => i.monitoringProfileId && i.monitoringProfileId !== currentProfileId
    );

    return {
      hasAssignedResources: assignedResources.length > 0,
      assignedResources,
    };
  };

  const assignResources = async () => {
    await confirmAssignInstances();
  };

  const confirmAssignInstances = async () => {
    setIsLoading(true);
    try {
      const assignInstancesPayload: AssignResourceModel = {
        clientId,
        monitoringProfileId: isEditMode
          ? (currentProfile?.id as number)
          : (createdProfileId as number),
        assignments: selectedResources.map((i) => ({
          resourceId: parseInt(i.key),
        })),
      };

      const result = await AlertsService.assignResources(assignInstancesPayload);

      if (result) {
        // refetch && refetch();
        getProfiles();
        setExpandedIds([]);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
      setIsDirty(false);
    }
  };

  const assignAlertTypes = async () => {
    setIsLoading(true);

    const assignments = selectedAlertTypes
      .filter((alertType) => alertType.checked === "on")
      .map((alertType) => ({
        alertTypeId: alertType.id,
        alertSubtypeId: alertType.subtypeId ?? null,
        severityTypeId: alertType.severityTypeId as 1 | 3 | 5,
        thresholdId: alertType.thresholdId ?? 0,
        objectAssignments: (alertType.selectedObjectNames ?? []).map((name) => ({ name })),
        alertLevel: alertType.alertLevel,
      }));

    if (assignments.length === 0) {
      Emitter.emit("displayFailure", "Please select at least one alert type.");
      setIsLoading(false);
      return;
    }

    try {
      const assignAlertTypesPayload: AssignAlertTypeModel = {
        clientId,
        monitoringProfileId:
          isEditMode && currentProfile ? currentProfile.id : (createdProfileId as number),
        assignments,
      };
      const result = await AlertsService.assignAlertType(assignAlertTypesPayload);
      if (result?.monitoringProfileId) {
        // refetch && refetch();
        getProfiles();
        setExpandedIds([]);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
      setIsDirty(false);
    }
  };

  const assignNotificationChannels = async () => {
    setIsLoading(true);

    try {
      const notificationChannelPayload: AssignNotificationChannelsModel = {
        clientId,
        monitoringProfileId:
          isEditMode && currentProfile ? currentProfile.id : (createdProfileId as number),
        alertOpenAssignments: selectedNotificationChannels.alertOpenAssignments,
        alertCloseAssignments: selectedNotificationChannels.alertCloseAssignments,
      };

      const result = await AlertsService.assignNotificationChannels(notificationChannelPayload);

      if (result) {
        // refetch && refetch();
        getProfiles();
        setExpandedIds([]);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
      setIsDirty(false);
    }
  };

  const getRemovedAlertTypes = () => {
    if (!isEditMode || !currentProfile?.alertTypeAssignments) return [];

    const currentAlertKeys = new Set(
      selectedAlertTypes.map(
        (alert) => `${alert.id}-${alert.subtypeId ?? "null"}-${alert.alertLevel || 1}`
      )
    );

    return currentProfile.alertTypeAssignments
      .filter((originalAlert) => {
        const originalKey = `${originalAlert.alertTypeId}-${originalAlert.alertSubTypeId ?? "null"}-${originalAlert.alertLevel}`;
        return !currentAlertKeys.has(originalKey);
      })
      .map((alert) => ({
        name: alert.name || "Unknown Alert Type",
        level: alert.alertLevel,
      }));
  };

  const getAddedAlertTypes = () => {
    if (!isEditMode || !currentProfile?.alertTypeAssignments) return [];

    const currentAlertKeys = new Set(
      currentProfile.alertTypeAssignments.map(
        (alert) => `${alert.alertTypeId}-${alert.alertSubTypeId ?? "null"}-${alert.alertLevel}`
      )
    );

    return selectedAlertTypes
      .filter((newAlert) => {
        const newKey = `${newAlert.id}-${newAlert.subtypeId ?? "null"}-${newAlert.alertLevel || 1}`;
        return !currentAlertKeys.has(newKey);
      })
      .map((alert) => ({
        name: alert.name || "Unknown Alert Type",
        level: alert.alertLevel || 1,
      }));
  };

  const handleContinue = async () => {
    if (currentStep === 1) {
      if (!isNameValid) {
        setNameTouched(true);
        setDescriptionTouched(true);
        return;
      }

      if (isEditMode) {
        await updateProfileDescription();
      } else {
        await createProfile();
      }
    }

    if (currentStep === 2) {
      await assignResources();
      if (!isEditMode) setCurrentStep(currentStep + 1);
    }

    if (currentStep === 3) {
      // Check for removed alert types in edit mode
      if (isEditMode) {
        const removedTypes = getRemovedAlertTypes();
        const addedTypes = getAddedAlertTypes();
        if (removedTypes.length > 0 || addedTypes.length > 0) {
          setRemovedAlertTypes(removedTypes);
          setAddedAlertTypes(addedTypes);
          setIsAlertRemovalConfirmModalOpen(true);
          return;
        }
      }

      await assignAlertTypes();
      if (!isEditMode) setCurrentStep(currentStep + 1);
    }

    if (currentStep === 4) {
      await assignNotificationChannels();
      if (!isEditMode) {
        setIsProfileCreatedModalOpen(true);
      }
    }
  };

  const handleConfirmAlertRemoval = async () => {
    setIsAlertRemovalConfirmModalOpen(false);
    await assignAlertTypes();
    if (!isEditMode) setCurrentStep(currentStep + 1);

    if (isAlertRemovalFromCloseFlow) {
      setIsAlertRemovalFromCloseFlow(false);
      setIsModalOpen(false);
    }
  };

  const handleCancelAlertRemoval = () => {
    // Add the removed alert types back to selectedAlertTypes
    const restoredAlertTypes = removedAlertTypes
      .map((removedAlert) => {
        const originalAlert = currentProfile?.alertTypeAssignments?.find(
          (alert) => alert.name === removedAlert.name && alert.alertLevel === removedAlert.level
        );

        if (originalAlert) {
          const assignedObjectNames = (originalAlert.objectAssignments || []).map(
            (obj) => obj.name
          );
          return {
            id: originalAlert.alertTypeId,
            subtypeId: originalAlert.alertSubTypeId,
            name: originalAlert.name ?? "",
            severityTypeId: originalAlert.severityTypeId,
            severityTypeName: originalAlert.severityTypeName ?? "",
            thresholdId: originalAlert.thresholdId ?? 0,
            formula: "",
            checked: "on" as const,
            tableChecked: undefined,
            objectAssignments: originalAlert.objectAssignments ?? [],
            selectedObjectNames: [...assignedObjectNames],
            alertLevel: originalAlert.alertLevel,
            thresholds: originalAlert.thresholds ?? [],
            objectName: originalAlert.objectName,
          };
        }
        return null;
      })
      .filter(Boolean) as AlertType[];

    const addedAlertKeys = new Set(addedAlertTypes.map((alert) => `${alert.name}-${alert.level}`));

    const filteredSelectedAlertTypes = selectedAlertTypes.filter((alert) => {
      const alertKey = `${alert.name}-${alert.alertLevel || 1}`;
      return !addedAlertKeys.has(alertKey);
    });

    setSelectedAlertTypes(
      [...filteredSelectedAlertTypes, ...restoredAlertTypes]?.filter((i) => i?.id !== 0)
    );
    setIsAlertRemovalConfirmModalOpen(false);

    if (isAlertRemovalFromCloseFlow) {
      setIsAlertRemovalFromCloseFlow(false);
      setIsModalOpen(false);
    }
  };

  const handleBack = async () => {
    if (isDirty) {
      if (currentStep === 2) {
        await assignResources();
      }

      if (currentStep === 3) {
        await assignAlertTypes();
      }

      if (currentStep === 4) {
        await assignNotificationChannels();
      }
    }
    setCurrentStep(currentStep - 1);
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      if (!isNameValid) {
        setNameTouched(true);
        setDescriptionTouched(true);
        return;
      }

      if (isDirty) {
        if (isEditMode) {
          await updateProfileDescription();
        } else {
          await createProfile();
        }
      }
      setCurrentStep(currentStep + 1);
    }

    if (currentStep === 2) {
      if (isDirty) {
        await assignResources();
      }
      setCurrentStep(currentStep + 1);
    }

    if (currentStep === 3) {
      if (isDirty) {
        await assignAlertTypes();
      }
      setCurrentStep(currentStep + 1);
    }
  };

  const handleAddTimeFrame = () => {
    setIsDirty(true);
    const timeFrames = [...profileDescription.timeFrames];
    timeFrames.push({
      timePeriod: "weekdays",
      startTime: moment().startOf("day"),
      endTime: moment().startOf("day"),
    });
    setProfileDescription({ ...profileDescription, timeFrames });
  };

  const handleRemoveTimeFrame = (index: number) => {
    setIsDirty(true);
    if (profileDescription.timeFrames.length <= 1) return;
    const timeFrames = profileDescription.timeFrames;
    timeFrames.splice(index, 1);
    setProfileDescription({ ...profileDescription, timeFrames });
  };

  const handleChangeSelectedResources = (resources: Resource[]) => {
    setIsDirty(true);
    const updatedSelections = resources.map((i) => ({
      ...i,
      monitoringProfileId: createdProfileId || i.monitoringProfileId,
      monitoringProfileName: profileDescription.name || i.monitoringProfileName,
    }));

    //@ts-ignore
    setSelectedResources(updatedSelections);
    setProfileDescription((prev) => ({
      ...prev,
      assignInstanceIds: updatedSelections.map((i) => parseInt(i.key)),
    }));
  };

  const isStep4Invalid =
    selectedNotificationChannels.alertOpenAssignments.length === 0 &&
    selectedNotificationChannels.alertCloseAssignments.length === 0 &&
    !isEditMode;
  const isStep1Incomplete = currentStep === 1 && !isNameValid;
  // const isStep2Incomplete = currentStep === 2 && selectedInstances.length === 0 && !isEditMode;
  const isStep2Incomplete = false;
  const isStep3Incomplete = currentStep === 3 && selectedAlertTypes.length === 0 && !isEditMode;
  const isStep4Incomplete = currentStep === 4 && isStep4Invalid;
  const isFormIncomplete =
    isStep1Incomplete || isStep2Incomplete || isStep3Incomplete || isStep4Incomplete;

  const renderRightButtons = () => (
    <>
      <EuiFlexItem grow={false}>
        <Button
          type="secondary"
          text={currentStep === 1 && !isEditMode ? "Cancel" : "Close"}
          onClick={handleCloseModal}
          disabled={isLoading}
        />
      </EuiFlexItem>
      <EuiFlexItem grow={false}>
        <Button
          type="primary"
          onClick={handleContinue}
          text={currentStep !== 4 && !isEditMode ? "Continue" : "Save"}
          isLoading={isLoading}
          disabled={isLoading || isFormIncomplete}
        />
      </EuiFlexItem>
    </>
  );

  const NotificationChannelModalFooter = () => {
    return (
      <>
        <EuiFlexItem grow={false}>
          <Button
            type="secondary"
            text="I'll do it later"
            onClick={() => setisNotificationChannelModalOpen(false)}
          />
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <Button
            type="primary"
            text="Yes"
            onClick={() => {
              setisNotificationChannelModalOpen(false);
              navigate(REDIRECT_TO_NOTIFICATION_CHANNELS);
            }}
          />
        </EuiFlexItem>
      </>
    );
  };

  const CloseConfirmationModalFooter = () => {
    return (
      <>
        <EuiFlexItem grow={false}>
          <Button
            type="secondary"
            text="No"
            onClick={() => {
              refetch();
              setIsCloseConfirmationModalOpen(false);
              setIsModalOpen(false);
            }}
          />
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <Button
            type="primary"
            text="Yes"
            onClick={() => {
              if (currentStep === 3) {
                // Calculate and set the alert types before opening the modal
                const removedTypes = getRemovedAlertTypes();
                const addedTypes = getAddedAlertTypes();
                setRemovedAlertTypes(removedTypes);
                setAddedAlertTypes(addedTypes);
                setIsCloseConfirmationModalOpen(false);
                setIsAlertRemovalFromCloseFlow(true);
                setIsAlertRemovalConfirmModalOpen(true);
              } else {
                setIsCloseConfirmationModalOpen(false);
                handleContinue();
                //setIsModalOpen(false);
              }
            }}
          />
        </EuiFlexItem>
      </>
    );
  };

  const handleProfileDetailsUpdate = (update: Partial<NewProfileData>) => {
    setIsDirty(true);
    setProfileDescription((prev) => ({
      ...prev,
      ...update,
    }));
  };

  const handleSetExistingProfile = (profile: Profile) => {
    if (!profile) return;

    const name =
      isEditMode && currentProfile && profile.id === currentProfile.id
        ? currentProfile.name
        : `${profile.name} - copy`;

    setProfileDescription({
      existingProfile: profile.existingProfile || null,
      name,
      activeTimes: profile.timeZone ?? "",
      description: profile.description ?? "",
      isSyncedToClients: profile.isSyncedToClients ?? false,
      timeFrames: (profile.activityWindows ?? []).map((timeFrame) => ({
        timePeriod:
          timeFrame.daysOfWeek?.length === 7
            ? "daily"
            : timeFrame.daysOfWeek?.length === 5
              ? "weekdays"
              : "weekends",
        startTime: moment(timeFrame.startTime ?? "", "HH:mm:ss"),
        endTime: moment(timeFrame.endTime ?? "", "HH:mm:ss"),
      })),
    });

    if (isEditMode && currentProfile && profile.id === currentProfile.id) {
      setSelectedResources(
        (profile.resourceAssignments ?? []).map((i) => ({
          ...i,
          key: i.resourceId?.toString() ?? "",
          label: i.resourceName ?? "",
          checked: undefined,
          monitoringProfileId: profile.id,
          monitoringProfileName: profile.name ?? "",
        }))
      );
    } else {
      setSelectedResources([]);
    }

    setSelectedAlertTypes(
      (profile.alertTypeAssignments ?? [])
        ?.filter((i) => i?.alertTypeId !== 0)
        ?.map((alertType) => {
          const assignedObjectNames = (alertType.objectAssignments || []).map((obj) => obj.name);
          return {
            id: alertType.alertTypeId,
            subtypeId: alertType.alertSubTypeId,
            name: alertType.name ?? "",
            severityTypeId: alertType.severityTypeId,
            severityTypeName: alertType.severityTypeName ?? "",
            thresholdId: alertType.thresholdId ?? 0,
            formula: "",
            checked: "on",
            tableChecked: undefined,
            objectAssignments: alertType.objectAssignments ?? [],
            selectedObjectNames: [...assignedObjectNames],
            alertLevel: alertType.alertLevel,
            thresholds: alertType.thresholds ?? [],
            objectName: alertType.objectName,
          };
        })
    );

    const processNotificationMappings = (
      mappings:
        | Array<{
            id: number;
            name: string;
            type: string;
            severityAssignments?: Array<{
              id: number;
              name: string;
            }>;
          }>
        | undefined
    ): { severityTypeId: number; channels: { channelId: number }[] }[] => {
      if (!mappings) return [];
      const assignmentsMap = new Map<number, { channelId: number }[]>();

      mappings.forEach((channelMapping) => {
        (channelMapping.severityAssignments ?? []).forEach((severityAssignment) => {
          const severityTypeId = severityAssignment.id;
          const channelId = channelMapping.id;

          if (!assignmentsMap.has(severityTypeId)) {
            assignmentsMap.set(severityTypeId, []);
          }
          const channelsForSeverity = assignmentsMap.get(severityTypeId)!;
          if (!channelsForSeverity.some((c) => c.channelId === channelId)) {
            channelsForSeverity.push({ channelId });
          }
        });
      });

      return Array.from(assignmentsMap.entries()).map(([severityTypeId, channels]) => ({
        severityTypeId,
        channels,
      }));
    };

    setSelectedNotificationChannels({
      alertOpenAssignments: processNotificationMappings(
        profile.alertOpenNotificationChannelMappings
      ),
      alertCloseAssignments: processNotificationMappings(
        profile.alertCloseNotificationChannelMappings
      ),
    });
  };

  useEffect(() => {
    if (isEditMode && currentProfile) {
      handleSetExistingProfile(currentProfile);
    }
  }, [isEditMode, currentProfile]);

  const getUpdatedAlertTypes = () => {
    const id = isEditMode ? (currentProfile?.id as number) : (createdProfileId as number);
    return profiles.find((profile) => profile.id === id)?.alertTypeAssignments ?? [];
  };

  const handleCloseModal = () => {
    if (isDirty) {
      setIsCloseConfirmationModalOpen(true);
    } else {
      refetch();
      setIsModalOpen(false);
    }
  };

  const monitoringProfileId: any = isEditMode ? currentProfile?.id : createdProfileId;

  return (
    <>
      <Modal
        title={
          currentProfile
            ? `Edit Profile: ${currentProfile.name}`
            : `New Profile: ${profileDescription?.name || ""}`
        }
        onClose={handleCloseModal}
        maxWidth={currentStep === 3 ? 1200 : currentStep === 2 ? 1000 : 800}
        maxHeight={"90vh"}
        children={
          <div style={{ width: currentStep === 3 ? 1150 : currentStep === 2 ? 950 : 750 }}>
            <EuiFlexGroup>
              <EuiFlexItem grow={1}>
                <ProfileSteps
                  currentStep={currentStep}
                  setCurrentStep={setCurrentStep}
                  isEditMode={isEditMode}
                />
              </EuiFlexItem>
            </EuiFlexGroup>
            <EuiSpacer size="s" />
            <h2 className={classes.formHeading}>
              {PROFILE_STEPS.find((step) => step.number === currentStep)?.title || ""}
            </h2>
            {currentStep === 1 && (
              <ProfileDetails
                profileDescription={profileDescription}
                handleProfileDetailsUpdate={handleProfileDetailsUpdate}
                handleRemoveTimeFrame={handleRemoveTimeFrame}
                handleAddTimeFrame={handleAddTimeFrame}
                errorMsgs={errorMsgs}
                showNameError={showNameError}
                setNameTouched={setNameTouched}
                showDescriptionError={showDescriptionError}
                setDescriptionTouched={setDescriptionTouched}
                isSystemProfile={isSystemProfile}
                isEditMode={isEditMode}
              />
            )}
            {currentStep === 2 && (
              <ProfileResources
                selectedResources={selectedResources}
                setSelectedResources={setSelectedResources}
                handleChangeSelectedResources={handleChangeSelectedResources}
                checkPreviousAssignments={checkPreviousAssignments}
                setIsDirty={setIsDirty}
                isSystemProfile={isSystemProfile}
              />
            )}
            {currentStep === 3 && !isNullOrUndefined(monitoringProfileId) && (
              <ProfileAlertsAssign
                selectedAlertTypes={selectedAlertTypes}
                setSelectedAlertTypes={setSelectedAlertTypes}
                setIsDirty={setIsDirty}
                isSystemProfile={isSystemProfile}
                monitoringProfileId={monitoringProfileId}
              />
            )}
            {currentStep === 4 && (
              <ProfileNotifications
                selectedNotificationChannels={selectedNotificationChannels}
                setSelectedNotificationChannels={setSelectedNotificationChannels}
                getUpdatedAlertTypes={getUpdatedAlertTypes}
                setIsDirty={setIsDirty}
              />
            )}
          </div>
        }
        footer={
          <>
            <EuiFlexGroup gutterSize="m" justifyContent="spaceBetween">
              <EuiFlexGroup gutterSize="m">
                {currentStep > 1 && currentStep <= 4 && !(!isEditMode && currentStep === 2) && (
                  <EuiFlexItem grow={false}>
                    <Button text="Back" type="back" onClick={handleBack} disabled={isLoading} />
                  </EuiFlexItem>
                )}
                {currentStep < 4 && isEditMode && (
                  <EuiFlexItem grow={false}>
                    <Button
                      text="Next"
                      type="next"
                      onClick={handleNext}
                      disabled={isLoading || (currentStep === 1 && !isEditMode)}
                    />
                  </EuiFlexItem>
                )}
              </EuiFlexGroup>
              <EuiFlexGroup gutterSize="m" justifyContent="flexEnd">
                {renderRightButtons()}
              </EuiFlexGroup>
            </EuiFlexGroup>
          </>
        }
      />
      {isProfileCreatedModalOpen && (
        <ProfileCompleteModal
          refetch={refetch}
          setIsFormModalOpen={setIsModalOpen}
          setIsProfileCreatedModalOpen={setIsProfileCreatedModalOpen}
          profile={profilesData?.profiles?.find(
            (profile: any) => profile.id === createdProfileId || profile.id === monitoringProfileId
          )}
        />
      )}
      {isNotificationChannelModalOpen && (
        <Modal
          title="Before You Start"
          onClose={() => setisNotificationChannelModalOpen(false)}
          footer={<NotificationChannelModalFooter />}>
          <EuiText>
            <p>
              Enhance your monitoring system with notification channels. Would you like to begin by
              creating a custom notification channel?
            </p>
          </EuiText>
        </Modal>
      )}
      {isCloseConfirmationModalOpen && (
        <Modal
          title="Before You Go"
          onClose={() => setIsCloseConfirmationModalOpen(false)}
          footer={<CloseConfirmationModalFooter />}>
          <EuiText>
            <p>Do you want to save your progress before leaving this step?</p>
          </EuiText>
        </Modal>
      )}
      {isAlertRemovalConfirmModalOpen && (
        <ConfirmModal
          title="Update Alert Types"
          message={
            <div>
              {removedAlertTypes.length > 0 && (
                <>
                  <EuiText>
                    <p>
                      You are about to <strong>remove</strong> the following alert types from this
                      profile:
                    </p>
                  </EuiText>
                  <EuiSpacer size="s" />
                  <ul style={{ paddingLeft: "20px" }}>
                    {removedAlertTypes.map((alert, index) => (
                      <li key={index}>
                        {alert.name} (Level {alert.level})
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {addedAlertTypes.length > 0 && (
                <>
                  {removedAlertTypes.length > 0 && <EuiSpacer size="m" />}
                  <EuiText>
                    <p>
                      You are about to <strong>add</strong> the following alert types to this
                      profile:
                    </p>
                  </EuiText>
                  <EuiSpacer size="s" />
                  <ul style={{ paddingLeft: "20px" }}>
                    {addedAlertTypes.map((alert, index) => (
                      <li key={index}>
                        {alert.name} (Level {alert.level})
                      </li>
                    ))}
                  </ul>
                </>
              )}

              <EuiSpacer size="m" />
              <EuiText>
                <p>Do you want to continue?</p>
              </EuiText>
            </div>
          }
          onClose={handleCancelAlertRemoval}
          onConfirm={handleConfirmAlertRemoval}
          cancelButtonText="Cancel"
          confirmButtonText="Continue"
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export default ProfileForm;
